// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ClipboardCheck } from 'lucide-react'
import * as api from '../../api/client'

// ── Types (mirror GET /workbench/field-defs) ───────────────────────────────
interface StatusValue { value: string; sort_order: number; color_bg: string | null; color_text: string | null; retired?: boolean }
interface FieldDef { field_key: string; display_name: string; internal_name: string | null; field_kind: string; sort_order: number; values: StatusValue[] }

// A colored pill matching the WorkBench color matrix (mirrors StatePills shape).
// D-CV-4: a bare value reads as a one-element list; a JSON array as many (parseMulti contract).
export function parseChoiceList(v: string | null | undefined): string[] {
  if (!v) return []
  const t = v.trim()
  if (t.startsWith('[')) { try { const a = JSON.parse(t); return Array.isArray(a) ? a.map(String) : [v] } catch { return [v] } }
  return [v]
}

export function WorkBenchPill({ value, bg, fg }: { value: string; bg?: string | null; fg?: string | null }) {
  return (
    <span style={{
      display: 'inline-block',
      background: bg || '#f3f4f6',
      color: fg || '#374151',
      padding: '4px 8px 5px 8px',
      borderRadius: 16,
      height: 24,
      lineHeight: '15px',
      boxSizing: 'border-box',
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 260,
    }}>{value}</span>
  )
}

// A dropdown styled as the colored pill; selecting a value calls onChange.
function WorkBenchStatusSelect({
  def, current, disabled, onChange,
}: { def: FieldDef; current: string | null; disabled: boolean; onChange: (v: string | null) => void }) {
  const selected = useMemo(() => def.values.find(v => v.value === current) ?? null, [def.values, current])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <select
        value={current ?? ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
        style={{
          appearance: 'auto',
          fontSize: 12,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 16,
          border: '1px solid var(--color-border)',
          background: selected?.color_bg || '#fff',
          color: selected?.color_text || 'var(--color-text)',
          cursor: disabled ? 'default' : 'pointer',
          maxWidth: 280,
        }}
      >
        <option value="">— none —</option>
        {def.values.filter(v => !v.retired || v.value === current).map(v => (
          <option key={v.value} value={v.value} style={{ background: '#fff', color: '#111' }}>{v.value}{v.retired ? ' (retired)' : ''}</option>
        ))}
      </select>
    </div>
  )
}

// ── Inline editable cell for table/queue views ─────────────────────────────
// Loads field-defs once via useWorkBenchDefs (caller passes the def for this field).
export function WorkBenchCell({
  fieldDef, rxId, current, onSaved,
}: { fieldDef?: FieldDefShape; rxId: string; current: string | null; onSaved?: (v: string | null) => void }) {
  const [value, setValue] = useState<string | null>(current)
  const [saving, setSaving] = useState(false)
  // D-WBFILL-2: a failed save used to revert the cell and say NOTHING. That is how the fill-page
  // 404 stayed invisible - a pharmacist set a status, watched it flick back, and was told nothing
  // had gone wrong. Fail-soft must mean "do not lose the page", never "do not mention it".
  const [saveError, setSaveError] = useState<string | null>(null)
  // CV-1e: the parent's `current` prop lags behind our own successful save (the row list
  // hasn't refetched yet). Without guarding, the effect below re-runs on the next parent
  // render and resets `value` back to the stale `current` — the dropdown snaps to "—" and
  // the edit looks like it didn't stick. Track what WE last wrote and ignore an incoming
  // `current` that still shows the pre-save value; only accept `current` once it catches
  // up (or genuinely changes from elsewhere).
  const lastSaved = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (lastSaved.current !== undefined && current !== lastSaved.current) {
      // `current` still disagrees with what we saved → it's stale, keep our value.
      return
    }
    // `current` matches our save (caught up) or we never saved → trust the prop.
    lastSaved.current = undefined
    setValue(current)
  }, [current])
  if (!fieldDef) {
    return current ? <WorkBenchPill value={current} /> : <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
  }
  const def = fieldDef
  const isMulti = (def as any).selection_mode === 'multi'
  const selectedList = parseChoiceList(value)
  const sel = def.values.find(v => v.value === value) ?? null
  // Pioneer-fed / derived / retired fields are never editable from the queue (spec §3.4).
  if (def.field_kind === 'pioneer' || def.field_kind === 'derived' || def.field_kind === 'retired') {
    return value ? <WorkBenchPill value={value} bg={sel?.color_bg} fg={sel?.color_text} /> : <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
  }
  if (isMulti) {
    // D-CV-4 checkbox mode: same option list, multiple selections, stored as a JSON array.
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <MultiChoiceCell
          options={def.values.filter(v => !v.retired)}
          selected={selectedList}
          saving={saving}
          onChange={(list) => change(list.length === 0 ? null : JSON.stringify(list))}
        />
        <FieldHistoryButton rxId={rxId} fieldKey={def.field_key} />
      </span>
    )
  }
  async function change(v: string | null) {
    setSaving(true); setSaveError(null)
    const prev = value
    setValue(v)
    try {
      const res = await api.apiFetch(`/rx/${rxId}/workbench`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { [def.field_key]: v } }),
      })
      if (!res.ok) throw res
      // CV-1e: remember what we persisted so the stale-prop effect can't revert it.
      lastSaved.current = v
      onSaved?.(v)
    } catch (e: any) {
      setValue(prev); lastSaved.current = undefined
      let msg = 'Could not save - the value was not changed.'
      try { const b = await e?.json?.(); if (b?.message || b?.error) msg = b.message || b.error } catch { /* non-JSON */ }
      setSaveError(msg)
    }
    finally { setSaving(false) }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value ?? ''}
      disabled={saving}
      // Stop the row's navigate-on-click from firing when editing in-place.
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      onChange={e => change(e.target.value === '' ? null : e.target.value)}
      style={{
        appearance: 'auto', fontSize: 12, fontWeight: 600,
        padding: '2px 6px', borderRadius: 16,
        border: '1px solid var(--color-border)',
        background: sel?.color_bg || '#fff',
        color: sel?.color_text || 'var(--color-text)',
        cursor: saving ? 'wait' : 'pointer', maxWidth: 180,
      }}
    >
      <option value="">—</option>
      {def.values.filter(v => !v.retired || v.value === value).map(v => <option key={v.value} value={v.value} style={{ background: '#fff', color: '#111' }}>{v.value}{v.retired ? ' (retired)' : ''}</option>)}
    </select>
    <FieldHistoryButton rxId={rxId} fieldKey={def.field_key} />
    {saveError && (
      <span role="alert" title={saveError}
        style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#991b1b', cursor: 'help' }}>
        not saved
      </span>
    )}
    </span>
  )
}

// Two ISO instants are "the same edit" when they land in the same MINUTE. `datetime-local`
// exposes minute granularity and toLocalInput drops seconds, so a stored value with seconds
// round-trips to a different ISO string without the user having changed anything. Comparing
// the strings would read that as an edit and write on every blur (i.e. on every tab through
// the grid). Compare the instants at the granularity the control actually exposes.
export function sameMinute(a: string | null, b: string | null): boolean {
  if (!a || !b) return !a && !b
  const ta = new Date(a).getTime(), tb = new Date(b).getTime()
  if (isNaN(ta) || isNaN(tb)) return a === b
  return Math.floor(ta / 60000) === Math.floor(tb / 60000)
}

// ── Inline editable DATE cell for list/queue views (WorkBench date fields, e.g. task_due_date) ──
// ASP-64 / ClickUp 86cb27v09: releases the inline-edit hold on the WorkBench due date. Uses the
// SAME write path as WorkBenchCell (PUT /rx/{id}/workbench) and the SAME datetime-local +
// toLocalInput/toISOString conversion the Rx-detail WorkBench panel uses, so the list edit and the
// detail edit agree and no timezone day-shift is introduced (the getValue on the column still
// returns raw ISO, so sort + the from/to date funnel are unaffected).
//
// COMMIT ON BLUR / ENTER — NOT on change. This is the one place this cell deliberately diverges
// from the choice-dropdown WorkBenchCell (where a `select` change IS the complete edit, so
// write-on-change is right). A `datetime-local` is typed segment by segment and its value stays
// EMPTY until the date AND the time are both complete, so a write-on-change date editor would:
//   1. fire a PUT that CLEARS the field once per segment while the user is still typing,
//   2. race those nulls against the real save (a trailing null can land last and silently blank
//      a date the UI is still showing — the CV-1e guard then blocks the refetch from correcting
//      it), and
//   3. flip `disabled` mid-entry, yanking focus out from under the cursor.
// Committing the DRAFT on blur/Enter collapses one edit into exactly one write. Escape reverts.
export function WorkBenchDateCell({
  fieldKey, rxId, current, label = 'Task Due Date', onSaved,
}: { fieldKey: string; rxId: string; current: string | null; label?: string; onSaved?: (v: string | null) => void }) {
  // `draft` is the RAW datetime-local string being typed — never an ISO value, and never
  // round-tripped through Date() until commit, so partial input is left alone.
  const [draft, setDraft] = useState<string>(() => toLocalInput(current))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // CV-1e: ignore a stale `current` prop until the row refetch catches up to our own save.
  const lastSaved = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const mine = lastSaved.current
    if (mine !== undefined && !sameMinute(current, mine)) return
    lastSaved.current = undefined
    setDraft(toLocalInput(current))
  }, [current])

  // What the server currently holds, as far as this cell knows.
  const baseline = () => (lastSaved.current !== undefined ? lastSaved.current : current)

  async function commit() {
    if (saving) return
    const iso = draft ? new Date(draft).toISOString() : null
    const base = baseline()
    // A blur with no edit (tabbing across the grid) must not write. Clearing a set date IS an
    // edit — sameMinute treats null-vs-value as different, so the clear still commits.
    if (sameMinute(iso, base)) { setError(null); return }
    setSaving(true); setError(null)
    try {
      const res = await api.apiFetch(`/rx/${rxId}/workbench`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { [fieldKey]: iso } }),
      })
      if (!res.ok) {
        // Surface the backend's SPECIFIC reason (bad_date / unknown_field / rx_wrong_pharmacy)
        // the way the Rx-detail panel does (Patrick 2026-08-06). A silent revert in a grid is
        // indistinguishable from a successful save.
        let msg = 'Save failed — edit and press Enter to retry.'
        try { const b = await res.json(); if (b?.message) msg = String(b.message) } catch { /* non-JSON body */ }
        setDraft(toLocalInput(base))
        setError(msg)
        return
      }
      lastSaved.current = iso
      setError(null)
      onSaved?.(iso)
    } catch {
      setDraft(toLocalInput(base))
      setError('Save failed — check your connection, then edit and press Enter to retry.')
    } finally { setSaving(false) }
  }

  return (
    <input
      type="datetime-local"
      value={draft}
      disabled={saving}
      // Don't let the row's navigate-on-click fire while editing in place.
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter') { e.preventDefault(); void commit() }
        else if (e.key === 'Escape') { e.preventDefault(); setDraft(toLocalInput(baseline())); setError(null) }
      }}
      onChange={e => setDraft(e.target.value)}   // LOCAL ONLY — typing never writes
      onBlur={() => { void commit() }}
      title={error ?? undefined}
      aria-label={label}
      aria-invalid={error ? true : undefined}
      style={{
        ...dateInputStyle, maxWidth: 172,
        cursor: saving ? 'wait' : 'text',
        borderColor: error ? 'var(--color-danger)' : undefined,
      }}
    />
  )
}

export interface FieldDefShape { field_key: string; display_name: string; field_kind?: string; values: StatusValue[]; selection_mode?: 'single' | 'multi' }

// Hook: load all WorkBench field-defs once, return a map keyed by field_key.
// RX-PERF-7: TTL-cached via cachedCfg — every queue tab mounts this hook, and the defs are
// admin config that changes rarely; tab switching should not refetch them each time.
export function useWorkBenchDefs(): Record<string, FieldDefShape> {
  const [defs, setDefs] = useState<Record<string, FieldDefShape>>({})
  useEffect(() => {
    let cancelled = false
    api.cachedCfg('workbench-field-defs', () =>
      api.apiFetch(`/workbench/field-defs`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { fields: [] }))
      .then(d => { if (!cancelled) {
        const m: Record<string, FieldDefShape> = {}
        for (const f of (d.fields ?? [])) m[f.field_key] = f
        setDefs(m)
      }})
      .catch(() => {})
    return () => { cancelled = true }
  }, [])
  return defs
}

// The full WorkBench/Operations section for the Rx detail page.
/** D-WBFILL-3 §2.6: one fill's identity, as the WorkBench GET now reports it. */
interface FillRef {
  fill_item_id: string
  display_rx_number: string | null
  fill_number: number | null
  date_filled_utc: string | null
}

/** "104750 · original fill, filled 05/06/2026" — never the bare Rx number on its own. */
function describeFill(f: FillRef | null): string {
  if (!f) return 'this prescription'
  const name = f.display_rx_number ?? 'this fill'
  const which = f.fill_number === 0 ? 'original fill' : `refill ${f.fill_number}`
  const when = f.date_filled_utc
    ? `, filled ${new Date(f.date_filled_utc).toLocaleDateString()}`
    : ''
  return `${name} · ${which}${when}`
}

export default function WorkBenchSection({ rxId, canEdit }: { rxId: string; canEdit: boolean }) {
  const [defs, setDefs] = useState<FieldDef[]>([])
  const [values, setValues] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // D-WBFILL-3 §2.6: which fill a save lands on, and which fill the values on screen came from.
  const [editsFill, setEditsFill] = useState<FillRef | null>(null)
  const [valuesFill, setValuesFill] = useState<FillRef | null>(null)
  // Phase 2: does this fill own its values, or is it reading the prescription's?
  const [fillOwnsValues, setFillOwnsValues] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [d, v] = await Promise.all([
          api.apiFetch(`/workbench/field-defs`, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject(r)),
          api.apiFetch(`/rx/${rxId}/workbench`, { credentials: 'include' }).then(r => r.ok ? r.json() : { workbench: null }),
        ])
        if (cancelled) return
        setDefs(d.fields ?? [])
        setValues(v.workbench ?? {})
        setEditsFill(v.edits_fill ?? null)
        setValuesFill(v.values_fill ?? null)
        // D-WBFILL-3 Phase 2: true once this fill has its own row, i.e. the values on screen are
        // this dispense's own rather than the prescription's. Every fill created before Phase 2
        // reports false and keeps the inherited-value notice.
        setFillOwnsValues(v.fill_owns_values === true)
        setLoading(false)
      } catch {
        if (!cancelled) { setError('Could not load WorkBench fields.'); setLoading(false) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [rxId])

  async function update(fieldKey: string, value: string | null) {
    setSaving(fieldKey); setError(null)
    const prev = values[fieldKey] ?? null
    setValues(s => ({ ...s, [fieldKey]: value }))   // optimistic
    try {
      const res = await api.apiFetch(`/rx/${rxId}/workbench`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { [fieldKey]: value } }),
      })
      if (!res.ok) {
        // Surface the backend's SPECIFIC reason (bad_value / single_select_field /
        // field_server_managed / bad_date) instead of a generic message, so staff can
        // see WHY a save was rejected and self-correct (Patrick 2026-08-06).
        let msg = 'Update failed — please retry.'
        try { const err = await res.json(); if (err?.message) msg = String(err.message) } catch { /* non-JSON body */ }
        setValues(s => ({ ...s, [fieldKey]: prev }))   // revert
        setError(msg)
        return
      }
      const d = await res.json()
      setValues(d.workbench ?? {})
      // The save landed on THIS fill, so the values on screen now belong to it — clear the
      // "set on another fill" notice rather than leaving a stale warning up. Under Phase 2 the
      // save also created this fill's own row, which is what the response reports.
      setValuesFill(editsFill)
      if (d.fill_owns_values === true) setFillOwnsValues(true)
    } catch {
      setValues(s => ({ ...s, [fieldKey]: prev }))   // revert
      setError('Update failed — please retry.')
    } finally {
      setSaving(null)
    }
  }

  if (loading) return (
    <section style={cardStyle}>
      <header style={sectionHeader}><ClipboardCheck size={14} aria-hidden="true" /><h3 style={sectionH3}>WorkBench / Operations</h3></header>
      <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading…</div>
    </section>
  )

  return (
    <section style={cardStyle}>
      <header style={sectionHeader}>
        <ClipboardCheck size={14} aria-hidden="true" />
        <h3 style={sectionH3}>WorkBench / Operations</h3>
      </header>

      {/* D-WBFILL-3 §2.6 — SAY WHERE THE EDIT LANDS, BEFORE IT IS MADE.
          On Rx 104750 two edits were recorded against the original fill because that is the page
          the Rx list opens, and its display key is the bare Rx number — identical to the
          prescription's. The write was correct; the user could not see which fill they were on.
          Rendered only when the server names a fill, so a prescription page is unchanged. */}
      {editsFill && (
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '-2px 0 10px' }}>
          Changes here are recorded against <strong style={{ color: 'var(--color-text)' }}>{describeFill(editsFill)}</strong>.
        </div>
      )}

      {/* D-WBFILL-3 Phase 2: this fill has its own record, so the values are unambiguously its
          own. Said out loud because the ambiguity is what the whole programme exists to remove —
          and because a pharmacist who was burned by the shared row needs to see that it changed. */}
      {fillOwnsValues && editsFill && (
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '-2px 0 10px' }}>
          These values belong to <strong style={{ color: 'var(--color-text)' }}>{describeFill(editsFill)}</strong> alone.
        </div>
      )}

      {/* Provenance when this fill does NOT own a row and the prescription row speaks for a
          DIFFERENT fill. The server no longer shows that fill's per-fill answers here (ticket
          #1022), so per-fill fields read blank; prescription-level fields are still shared. */}
      {!fillOwnsValues && valuesFill && editsFill && valuesFill.fill_item_id !== editsFill.fill_item_id && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a',
                      borderRadius: 6, padding: '6px 10px', margin: '0 0 10px' }}>
          Per-fill values were last set on <strong>{describeFill(valuesFill)}</strong>; none are recorded
          for this fill yet. Prescription-level fields are shared across all fills.
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 8 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8, columnGap: 12, alignItems: 'center' }}>
        {defs.map(def => {
          const cur = (values[def.field_key] as string) ?? null
          // Spec §3.4/§3.6: Pioneer-fed + derived fields are value+color, never a dropdown;
          // retired fields (biopsy) disappear unless an old row still carries a value.
          const readOnly = def.field_kind === 'pioneer' || def.field_kind === 'derived' || def.field_kind === 'retired'
          if (def.field_kind === 'retired' && !cur) return null
          const pill = cur
            ? <WorkBenchPill value={cur} bg={def.values.find(v => v.value === cur)?.color_bg} fg={def.values.find(v => v.value === cur)?.color_text} />
            : <span style={{ color: 'var(--color-text-mute)', fontSize: 13 }}>—</span>
          return (
            <FieldRow key={def.field_key} label={def.display_name}>
              {canEdit && !readOnly
                ? <WorkBenchStatusSelect def={def} current={cur} disabled={saving === def.field_key} onChange={v => update(def.field_key, v)} />
                : pill}
            </FieldRow>
          )
        })}
        {/* W-1 dates & notes (D-WB-1 §3.5) */}
        {DATE_FIELDS.map(([key, label]) => (
          <FieldRow key={key} label={label}>
            {canEdit ? (
              <input
                type="datetime-local"
                value={toLocalInput(values[key])}
                disabled={saving === key}
                onChange={e => update(key, e.target.value ? new Date(e.target.value).toISOString() : null)}
                style={dateInputStyle}
                aria-label={label}
              />
            ) : <span style={{ fontSize: 13 }}>{values[key] ? new Date(values[key]).toLocaleString() : '—'}</span>}
          </FieldRow>
        ))}
        <FieldRow label="Fill Notes">
          {canEdit ? (
            <textarea
              defaultValue={(values['fill_notes'] as string) ?? ''}
              onBlur={e => { if ((e.target.value || null) !== ((values['fill_notes'] as string) || null)) update('fill_notes', e.target.value || null) }}
              rows={2}
              style={{ ...dateInputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
              aria-label="Fill Notes"
            />
          ) : <span style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{(values['fill_notes'] as string) || '—'}</span>}
        </FieldRow>
        <FieldRow label="Color Tag">
          {canEdit ? (
            <input
              defaultValue={(values['color_tag'] as string) ?? ''}
              onBlur={e => { if ((e.target.value || null) !== ((values['color_tag'] as string) || null)) update('color_tag', e.target.value || null) }}
              style={{ ...dateInputStyle, maxWidth: 160 }}
              aria-label="Color Tag"
            />
          ) : <span style={{ fontSize: 13 }}>{(values['color_tag'] as string) || '—'}</span>}
        </FieldRow>
      </div>
    </section>
  )
}

const DATE_FIELDS: Array<[string, string]> = [
  ['task_due_date', 'Task Due Date'],
  ['cutoff_date', 'Cut Off Date'],
  ['next_dose', 'Next Dose Due'],
  ['deliver_on', 'Deliver On'],
]

function toLocalInput(v: any): string {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const dateInputStyle: React.CSSProperties = {
  fontSize: 12.5,
  padding: '4px 8px',
  borderRadius: 8,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', fontWeight: 500 }}>{label}</div>
      <div>{children}</div>
    </>
  )
}

// Match RxDetailPage's card/section styles.
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  padding: 16,
  marginBottom: 12,
}
const sectionHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12,
  color: 'var(--color-text)',
}
const sectionH3: React.CSSProperties = { margin: 0, fontSize: 14, fontWeight: 600 }


// D-CV-4: checkbox popover for multi-select choice fields (same options as the dropdown).
// The list renders in a BODY PORTAL (the ASP-57 SearchableSelect lesson) — inside the
// prescriptions table the cell sits in a scroll/overflow container that CLIPS an
// absolutely-positioned popover, making the control look dead.
export function MultiChoiceCell({ options, selected, saving, onChange }: {
  options: { value: string; color_bg?: string | null; color_text?: string | null }[]
  selected: string[]
  saving: boolean
  onChange: (list: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<{ top: number; left: number; up: boolean } | null>(null)
  useLayoutEffect(() => {
    if (!open) { setRect(null); return }
    const place = () => {
      const el = rootRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom
      const up = spaceBelow < 300 && r.top > 300
      setRect({ top: up ? r.top : r.bottom, left: r.left, up })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [open])
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v])
  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={saving}
        style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', minWidth: 90, maxWidth: 220,
                 padding: '3px 8px', borderRadius: 8, border: '1px solid var(--color-border)',
                 background: '#fff', cursor: saving ? 'wait' : 'pointer', fontSize: 12 }}
        title="Select one or more"
      >
        {selected.length === 0
          ? <span style={{ color: 'var(--color-text-mute)' }}>—</span>
          : selected.map(v => {
              const o = options.find(x => x.value === v)
              return <WorkBenchPill key={v} value={v} bg={o?.color_bg} fg={o?.color_text} />
            })}
        <span style={{ marginLeft: 'auto', color: 'var(--color-text-mute)', fontSize: 10 }}>▾</span>
      </button>
      {open && rect && createPortal(
        <div ref={listRef}
             onClick={e => e.stopPropagation()}
             style={{ position: 'fixed', zIndex: 9999, left: rect.left,
                      top: rect.up ? undefined : rect.top + 4,
                      bottom: rect.up ? (window.innerHeight - rect.top + 4) : undefined,
                      minWidth: 210, background: '#fff', border: '1px solid var(--color-border)',
                      borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 6,
                      maxHeight: 290, overflowY: 'auto' }}>
          {options.map(o => (
            <label key={o.value} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 8px',
                                          borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              <WorkBenchPill value={o.value} bg={o.color_bg} fg={o.color_text} />
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}


// CV-1d: per-field change history popover — who changed it, when, from → to.
// Body portal (ASP-57). Values shown are audit snapshots; free-text custom fields
// never log their value (Sasha ruling) — workbench fields are choice values, always shown.
export function FieldHistoryButton({ rxId, fieldKey }: { rxId: string; fieldKey: string }) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<{ changed_by: string | null; from: string | null; to: string | null; at: string }[] | null>(null)
  // Which grain the server answered for. A fill row shows that fill's own history only — a refill
  // is a distinct prescription, so showing a sibling dispense's edits here would misattribute them.
  const [scope, setScope] = useState<'fill' | 'prescription' | null>(null)
  const rootRef = useRef<HTMLSpanElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<{ top: number; left: number; up: boolean } | null>(null)
  useLayoutEffect(() => {
    if (!open) { setRect(null); return }
    const place = () => {
      const el = rootRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const up = window.innerHeight - r.bottom < 260 && r.top > 260
      setRect({ top: up ? r.top : r.bottom, left: Math.max(8, r.left - 140), up })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [open])
  useEffect(() => {
    if (!open) return
    setItems(null)
    setScope(null)
    api.getWorkbenchFieldHistory(rxId, fieldKey)
      .then(r => { setItems(r.items); setScope(r.scope) })
      .catch(() => { setItems([]); setScope(null) })
    const h = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open, rxId, fieldKey])
  return (
    <span ref={rootRef} onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <button type="button" onClick={() => setOpen(o => !o)} title="Change history"
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 3px',
                 color: 'var(--color-text-mute)', fontSize: 12, lineHeight: 1 }}>⏱</button>
      {open && rect && createPortal(
        <div ref={listRef} onClick={e => e.stopPropagation()}
             style={{ position: 'fixed', zIndex: 9999, left: rect.left,
                      top: rect.up ? undefined : rect.top + 4,
                      bottom: rect.up ? (window.innerHeight - rect.top + 4) : undefined,
                      width: 300, background: '#fff', border: '1px solid var(--color-border)',
                      borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 10,
                      maxHeight: 280, overflowY: 'auto', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: scope ? 2 : 6 }}>Change history</div>
          {/* Say whose history this is. Unlabelled, a fill's list reads as the prescription's. */}
          {scope && (
            <div style={{ color: 'var(--color-text-mute)', marginBottom: 6 }}>
              {scope === 'fill' ? 'This fill only' : 'This prescription'}
            </div>
          )}
          {items === null && <div style={{ color: 'var(--color-text-mute)' }}>Loading…</div>}
          {items?.length === 0 && (
            <div style={{ color: 'var(--color-text-mute)' }}>
              {scope === 'fill'
                ? 'No recorded changes for this field on this fill.'
                : 'No recorded changes for this field.'}
            </div>
          )}
          {items?.map((it, i) => (
            <div key={i} style={{ padding: '6px 0', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
              <div>
                <span style={{ color: 'var(--color-text-mute)' }}>{it.from ?? '—'}</span>
                {' → '}
                <strong>{it.to ?? '—'}</strong>
              </div>
              <div style={{ color: 'var(--color-text-mute)', marginTop: 2 }}>
                {it.changed_by ?? 'unknown'} · {new Date(it.at).toLocaleString('en-US')}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </span>
  )
}
